import type { LifecycleStatus, QuoteData } from 'lib/stores/tickerQuotesStore';
import type { ForecastAccuracySubject } from 'utils/forecastAccuracy';

import {
    bucketForDemProbability,
    fillForBucket,
    PARTY_BUCKETS,
    type DemWinBucket,
    type PartyBucket,
} from './raceBuckets';
import {
    demWinProbabilityFromMarkets,
    demWinProbabilityFromPartyProbabilities,
    partyMarkets,
    sameMajorPartyFromMarkets,
    type MajorRaceParty,
    type RaceEvent,
} from './raceMarketMath';
import { RACELESS_REGION_FILL } from './regionFillRules';
import { yesProbabilityFromCents } from './yesProbability';

/**
 * Domain logic for the hub's forecast cards: race extraction, seat/favored
 * counts, bar segments, and the live-quote overlay. Shared by the
 * chamber-control card (senate/house) and the governor forecast card, which
 * counts favored races instead of chamber control. Client-safe on purpose —
 * the cards recompute everything in the browser as WS quotes arrive, so
 * nothing here may import server plumbing (`raceMarkets.ts` and friends stay
 * on the server).
 *
 * The chamber data config (control event tickers, seats to win, sitting
 * seats) and the rating scale come from product-metadata via
 * ./midtermsMaps.ts and travel on `ChamberControlData`; only display copy
 * lives here.
 */

export const CHAMBER_LABELS = { house: 'the House', senate: 'the Senate' } as const;

export type Chamber = keyof typeof CHAMBER_LABELS;

/**
 * The maps whose live prices include each chamber's control event (each map
 * leads with it), i.e. what the compact Balance of Power cards poll.
 */
export const CHAMBER_CONTROL_MAPS: readonly Chamber[] = ['senate', 'house'];

export interface ControlRace {
    /** District id (`FL07`) or state postal code (`GA`) — the map path id. */
    regionId: string;
    /**
     * Snapshot Democratic-keyed rating probability (0–1), derived from the
     * higher-priced party exactly like the map fills. Null = no usable party market.
     */
    demProb: number | null;
    /** Party market tickers for WS subscription; null when the event has none. */
    demTicker: string | null;
    repTicker: string | null;
    /** Both candidates share this party, making the seat safe regardless of candidate pricing. */
    sameParty?: MajorRaceParty;
}

export interface ControlPartyMarket {
    /** The party's own market probability (0–1); null when unpriced. */
    probability: number | null;
    ticker: string | null;
}

export interface ChamberControlData {
    chamber: Chamber;
    seatsToWin: number;
    /** Seats not up in 2026, by caucus; { dem: 0, rep: 0 } for the House. */
    sittingSeats: { dem: number; rep: number };
    /** Rating scale from the same metadata snapshot as the map fills. */
    buckets: readonly DemWinBucket[];
    /** The CONTROL{H|S}-2026 chamber-control event's party markets. */
    control: { dem: ControlPartyMarket; rep: ControlPartyMarket };
    /** The CONTROL{H|S}-2026 chamber-control event ticker, for the card headline's link to that market. */
    controlEventTicker: string;
    /** What the card's forecast-accuracy pill describes: the control event itself (see `forecastAccuracySubjectByTicker`). */
    accuracy: ForecastAccuracySubject;
    races: ControlRace[];
}

/**
 * Every mapped race's dem-keyed win probability plus party market tickers,
 * from a single events payload. Events are matched by exact ticker against the
 * region map; anything else — unmapped events, a chamber-control event whose
 * ticker isn't a region — is ignored. Shared by the chamber-control snapshot
 * (which pulls its control event separately) and the governor forecast, which
 * has no control event. Prices exactly like the map fills, so a race's rating
 * here always matches its map color.
 */
export function racesFromEvents(
    events: readonly RaceEvent[],
    regionEventTickers: Record<string, string>,
): ControlRace[] {
    const regionByTicker = new Map(Object.entries(regionEventTickers).map(([region, ticker]) => [ticker, region]));
    const races: ControlRace[] = [];
    const seenRegions = new Set<string>();
    for (const event of events) {
        const regionId = regionByTicker.get(event.ticker);
        if (regionId === undefined || seenRegions.has(regionId)) continue;
        seenRegions.add(regionId);
        const markets = event.markets ?? [];
        const { dem, rep } = partyMarkets(markets);
        const sameParty = sameMajorPartyFromMarkets(markets);
        races.push({
            regionId,
            demProb: demWinProbabilityFromMarkets(markets),
            demTicker: dem?.ticker_name ?? null,
            repTicker: rep?.ticker_name ?? null,
            ...(sameParty === null ? {} : { sameParty }),
        });
    }
    return races;
}

/**
 * Snapshot bucket membership by region id, for filtering the map from a bar
 * segment click. Derived from the same snapshot probabilities as the map
 * fills so the filter keeps exactly the regions painted in that bucket's
 * color; regions without a usable market are absent (never filterable in,
 * always grayed out while a filter is active).
 */
export function regionBucketsFromRaces(
    races: readonly ControlRace[],
    demWinBuckets: readonly DemWinBucket[],
): Record<string, PartyBucket> {
    const buckets: Record<string, PartyBucket> = {};
    for (const race of races) {
        if (race.demProb === null) continue;
        buckets[race.regionId] = bucketForDemProbability(demWinBuckets, race.demProb);
    }
    return buckets;
}

function bucketSeatTallies(
    races: readonly ControlRace[],
    demWinBuckets: readonly DemWinBucket[],
): Record<PartyBucket, number> {
    const tallies: Record<PartyBucket, number> = {
        'safe-d': 0,
        'likely-d': 0,
        'lean-d': 0,
        tossup: 0,
        'lean-r': 0,
        'likely-r': 0,
        'safe-r': 0,
    };
    for (const race of races) {
        if (race.demProb === null) continue;
        tallies[bucketForDemProbability(demWinBuckets, race.demProb)] += 1;
    }
    return tallies;
}

/**
 * The forecast cards' hold math: sitting seats plus every race rated at least
 * "lean" for the party, with the toss-up bucket's count alongside. Unpriced
 * races count for none of the three, so the numbers may not sum to the
 * chamber (or map) size.
 */
export function ratedHoldCounts(
    races: readonly ControlRace[],
    buckets: readonly DemWinBucket[],
    sittingSeats: { dem: number; rep: number },
): { dem: number; rep: number; tossup: number } {
    const tallies = bucketSeatTallies(races, buckets);
    return {
        dem: sittingSeats.dem + tallies['safe-d'] + tallies['likely-d'] + tallies['lean-d'],
        rep: sittingSeats.rep + tallies['safe-r'] + tallies['likely-r'] + tallies['lean-r'],
        tossup: tallies.tossup,
    };
}

/**
 * The big seat numbers: sitting seats plus every race rated at least "lean"
 * for the party. Tossups and unpriced races count for neither side, so the
 * two numbers may not sum to the chamber size.
 */
export function seatCounts(data: ChamberControlData): { dem: number; rep: number } {
    const { dem, rep } = ratedHoldCounts(data.races, data.buckets, data.sittingSeats);
    return { dem, rep };
}

/**
 * The party favored by a dem-keyed win probability. A tie at exactly 0.5 goes
 * to the Democrats — the single deterministic tie rule behind the rated-races
 * carousel. (`favoredControlParty` applies the same ties-to-Democrats
 * convention to its two market probs.)
 */
export function favoredParty(demProb: number): 'dem' | 'rep' {
    return demProb >= 0.5 ? 'dem' : 'rep';
}

type SegmentKey = PartyBucket | 'sitting-d' | 'sitting-r' | 'tossup-dem' | 'tossup-rep' | 'unmapped';

export interface BarSegment {
    key: SegmentKey;
    /** The filter bucket a click toggles; null = sitting seats, not clickable. */
    bucket: PartyBucket | null;
    seats: number;
    color: string;
    /**
     * Hover/accessible copy: rating + favored-party win% range + count
     * ("Lean Democratic: 55-70% (3)"), or the held-seat copy for sitting caps.
     */
    tooltip: string;
}

/** 50% black flattened over the party color — the bar's darkened end caps. */
const DARK_DEM_FILL = '#132e80';
const DARK_REP_FILL = '#6d0b0b';

export const BUCKET_LABELS: Record<PartyBucket, string> = {
    'safe-d': 'Safe Democratic',
    'likely-d': 'Likely Democratic',
    'lean-d': 'Lean Democratic',
    tossup: 'Toss-up',
    'lean-r': 'Lean Republican',
    'likely-r': 'Likely Republican',
    'safe-r': 'Safe Republican',
};

const PARTY_ADJECTIVES = { dem: 'Democratic', rep: 'Republican' } as const;

/**
 * The favored party's win-probability bounds for a rating bucket, as rounded
 * percentages. Buckets are dem-keyed [min, previous bucket's min);
 * Republican-side buckets mirror to the Republican's own probability so both
 * parties' ratings read identically on a symmetric scale. Null when the
 * bucket is not on the scale.
 */
export function bucketRangeBounds(
    demWinBuckets: readonly DemWinBucket[],
    bucket: PartyBucket,
): [lo: number, hi: number] | null {
    const index = demWinBuckets.findIndex((b) => b.key === bucket);
    if (index === -1) return null;
    const min = demWinBuckets[index]!.min;
    const max = index === 0 ? 1 : demWinBuckets[index - 1]!.min;
    const repSide = bucket.endsWith('-r');
    return [Math.round((repSide ? 1 - max : min) * 100), Math.round((repSide ? 1 - min : max) * 100)];
}

/**
 * A bucket's configured range bounds as tooltip copy ("55-70%"), matching
 * the ranges shown in the bucket-column header tooltips.
 */
export function bucketRangeLabel(demWinBuckets: readonly DemWinBucket[], bucket: PartyBucket): string {
    const bounds = bucketRangeBounds(demWinBuckets, bucket);
    if (bounds === null) return '';
    const [lo, hi] = bounds;
    return `${lo}-${hi}%`;
}

function bucketTooltip(demWinBuckets: readonly DemWinBucket[], bucket: PartyBucket, seats: number): string {
    return `${BUCKET_LABELS[bucket]}: ${bucketRangeLabel(demWinBuckets, bucket)} (${seats})`;
}

/**
 * The favored-party win% floor of a party's Lean bucket — the "at least X%
 * chance to win" behind the forecast cards' seat-count tooltips, so the copy
 * tracks the remotely tunable scale that `ratedHoldCounts` counts by. Falls
 * back to 50 if the scale is missing the bucket.
 */
export function leanWinThresholdPct(demWinBuckets: readonly DemWinBucket[], party: 'dem' | 'rep'): number {
    const bounds = bucketRangeBounds(demWinBuckets, party === 'dem' ? 'lean-d' : 'lean-r');
    return bounds?.[0] ?? 50;
}

type SittingOffice = 'Senator' | 'Governor';

function sittingTooltip(office: SittingOffice, party: 'dem' | 'rep', seats: number): string {
    return `Held by ${PARTY_ADJECTIVES[party]} ${office} - not up for re-election in 2026 (${seats})`;
}

interface RaceSegmentDef {
    key: PartyBucket;
    color: string;
}

/**
 * Rating-bucket bar fills shared with the maps. Sitting members use separate
 * dark end caps, while every forecast bucket keeps its configured map fill.
 */
function ownFillRaceDefs(demWinBuckets: readonly DemWinBucket[]): readonly RaceSegmentDef[] {
    return PARTY_BUCKETS.map((key) => ({ key, color: fillForBucket(demWinBuckets, key) }));
}

interface SittingCaps {
    seats: { dem: number; rep: number };
    /** Office noun in the held-seat tooltip ("Senator", "Governor"). */
    office: SittingOffice;
}

/** Rating buckets between optional sitting end caps; zero-seat segments are omitted. */
function buildSegments(
    races: readonly ControlRace[],
    demWinBuckets: readonly DemWinBucket[],
    defs: readonly RaceSegmentDef[],
    sitting: SittingCaps | null,
    /** Optionally replaces one bucket's segment with several (the House bar's toss-up split). */
    expandSegment?: (segment: BarSegment) => BarSegment[],
): BarSegment[] {
    const tallies = bucketSeatTallies(races, demWinBuckets);
    const bucketSegments: BarSegment[] = defs.flatMap((def) => {
        const segment: BarSegment = {
            key: def.key,
            bucket: def.key,
            seats: tallies[def.key],
            color: def.color,
            tooltip: bucketTooltip(demWinBuckets, def.key, tallies[def.key]),
        };
        return expandSegment === undefined ? [segment] : expandSegment(segment);
    });
    const segments =
        sitting === null
            ? bucketSegments
            : [
                  {
                      key: 'sitting-d' as const,
                      bucket: null,
                      seats: sitting.seats.dem,
                      color: DARK_DEM_FILL,
                      tooltip: sittingTooltip(sitting.office, 'dem', sitting.seats.dem),
                  },
                  ...bucketSegments,
                  {
                      key: 'sitting-r' as const,
                      bucket: null,
                      seats: sitting.seats.rep,
                      color: DARK_REP_FILL,
                      tooltip: sittingTooltip(sitting.office, 'rep', sitting.seats.rep),
                  },
              ];
    return segments.filter((segment) => segment.seats > 0);
}

/**
 * Ordered rating segments for the governor bar: every race bucketed like the
 * senate bar, with the governors not up for re-election in 2026 as the dark
 * end caps. Zero-seat segments are omitted.
 */
export function buildRaceBarSegments(
    races: readonly ControlRace[],
    demWinBuckets: readonly DemWinBucket[],
    /** Sitting governors not up in 2026 — hardcoded product copy from the card. */
    sittingGovernors: { dem: number; rep: number },
): BarSegment[] {
    return buildSegments(races, demWinBuckets, ownFillRaceDefs(demWinBuckets), {
        seats: sittingGovernors,
        office: 'Governor',
    });
}

/** Toss-up seats by leaning party; a 50/50 tie leans Democratic like `favoredParty`. */
function tossupLeanTallies(
    races: readonly ControlRace[],
    demWinBuckets: readonly DemWinBucket[],
): { dem: number; rep: number } {
    const tallies = { dem: 0, rep: 0 };
    for (const race of races) {
        if (race.demProb === null) continue;
        if (bucketForDemProbability(demWinBuckets, race.demProb) !== 'tossup') continue;
        tallies[favoredParty(race.demProb)] += 1;
    }
    return tallies;
}

/**
 * The leaning party's win range within the toss-up bucket — from the 50% tie
 * line to the bucket's edge on that party's side ("50-60%").
 */
function tossupLeanRangeLabel(demWinBuckets: readonly DemWinBucket[], party: 'dem' | 'rep'): string {
    const bounds = bucketRangeBounds(demWinBuckets, 'tossup');
    if (bounds === null) return '';
    return `50-${party === 'dem' ? bounds[1] : 100 - bounds[0]}%`;
}

function tossupLeanSegment(party: 'dem' | 'rep', seats: number, demWinBuckets: readonly DemWinBucket[]): BarSegment {
    return {
        key: party === 'dem' ? 'tossup-dem' : 'tossup-rep',
        bucket: 'tossup',
        seats,
        color: fillForBucket(demWinBuckets, 'tossup'),
        tooltip: `Toss-up, leaning ${PARTY_ADJECTIVES[party]}: ${tossupLeanRangeLabel(demWinBuckets, party)} (${seats})`,
    };
}

/**
 * The House bar: no sitting caps (every seat is up), and while any district
 * lacks a priced market at the displayed date — the common state at far-past
 * timeline ticks, where most race markets didn't exist yet — the toss-up
 * bucket splits into its Dem- and Rep-leaning halves around a gray count of
 * those unmapped districts, so the bar shows how much of the map is missing
 * markets. With every district priced it stays one combined toss-up.
 */
function buildHouseBarSegments(races: readonly ControlRace[], demWinBuckets: readonly DemWinBucket[]): BarSegment[] {
    const defs = ownFillRaceDefs(demWinBuckets);
    const unmapped = races.filter((race) => race.demProb === null).length;
    if (unmapped === 0) return buildSegments(races, demWinBuckets, defs, null);

    const leaning = tossupLeanTallies(races, demWinBuckets);
    return buildSegments(races, demWinBuckets, defs, null, (segment) =>
        segment.key === 'tossup'
            ? [
                  tossupLeanSegment('dem', leaning.dem, demWinBuckets),
                  {
                      key: 'unmapped',
                      bucket: null,
                      seats: unmapped,
                      color: RACELESS_REGION_FILL,
                      tooltip: `No race market yet (${unmapped})`,
                  },
                  tossupLeanSegment('rep', leaning.rep, demWinBuckets),
              ]
            : [segment],
    );
}

/** Ordered bar segments for the chamber; zero-seat segments are omitted. */
export function buildBarSegments(data: ChamberControlData): BarSegment[] {
    if (data.chamber === 'house') return buildHouseBarSegments(data.races, data.buckets);
    return buildSegments(data.races, data.buckets, ownFillRaceDefs(data.buckets), {
        seats: data.sittingSeats,
        office: 'Senator',
    });
}

/**
 * The headline's favored party and rounded percentage from the control
 * event's markets. Each party is priced by its own market, falling back to
 * the inverse of the other; ties go to the Democrats (deterministic). Null
 * when neither market is priced.
 */
export function favoredControlParty(
    control: ChamberControlData['control'],
): { party: 'dem' | 'rep'; pct: number } | null {
    const { dem, rep } = control;
    if (dem.probability === null && rep.probability === null) return null;
    const demProb = dem.probability ?? 1 - rep.probability!;
    const repProb = rep.probability ?? 1 - dem.probability!;
    const party = demProb >= repProb ? 'dem' : 'rep';
    return { party, pct: Math.round((party === 'dem' ? demProb : repProb) * 100) };
}

/**
 * The indefinite article for a headline percentage: "an 84% chance" but
 * "a 55% chance". The article follows how the number is read aloud, so over
 * the 0–100 range "an" is exactly eight, eleven, eighteen, and the eighties.
 */
const AN_PERCENTAGES = new Set([8, 11, 18]);

export function chanceArticle(pct: number): 'a' | 'an' {
    if (AN_PERCENTAGES.has(pct)) return 'an';
    return pct >= 80 && pct < 90 ? 'an' : 'a';
}

/**
 * The live fields the forecast overlays price from. A WS `LiveMarketData`
 * satisfies it; so do the logged-out polled prices, which carry nothing else
 * (see ./MidtermsLivePrices.tsx).
 */
export interface LivePrice {
    quote?: Pick<QuoteData, 'yesBid' | 'yesAsk' | 'price'>;
    status?: Pick<LifecycleStatus, 'result'>;
}

/**
 * The shared pricing policy (see ./yesProbability.ts) over a live price (WS
 * or polled), so live overlays price exactly like the server snapshot they
 * refine. Null = no usable quote (caller keeps the snapshot value).
 */
export function quoteYesProbability(quote: LivePrice['quote'], status: LivePrice['status']): number | null {
    return yesProbabilityFromCents({
        result: status?.result,
        bid: quote?.yesBid ?? 0,
        ask: quote?.yesAsk ?? 0,
        last: quote?.price ?? 0,
    });
}

/**
 * A ticker's live probability from the WS quote map, priced by the shared
 * policy above. Null = unwatched ticker or no usable quote yet (callers keep
 * the snapshot value). The single resolver behind every live overlay.
 */
export function liveQuoteProbability(live: Record<string, LivePrice>, ticker: string | null): number | null {
    const entry = ticker === null ? undefined : live[ticker];
    return entry ? quoteYesProbability(entry.quote, entry.status) : null;
}

/**
 * Overlays live WS quotes onto only the two chamber-control markets. Compact
 * forecast surfaces do not need the chamber's hundreds of race rows, so this
 * keeps their serialized props and per-tick work limited to the headline's
 * actual dependencies.
 */
export function overlayControlLive(
    control: ChamberControlData['control'],
    live: Record<string, LivePrice>,
): ChamberControlData['control'] {
    if (Object.keys(live).length === 0) return control;

    const overlayMarket = (market: ControlPartyMarket): ControlPartyMarket => {
        const probability = liveQuoteProbability(live, market.ticker);
        return probability === null || probability === market.probability ? market : { ...market, probability };
    };
    const dem = overlayMarket(control.dem);
    const rep = overlayMarket(control.rep);
    return dem === control.dem && rep === control.rep ? control : { dem, rep };
}

/**
 * Overlays live WS quotes onto a race list, preserving the snapshot's dem-keyed
 * higher-party semantics. Tickers without a
 * usable live quote keep their snapshot values; an empty quote map returns the
 * list untouched. Shared by the chamber-control overlay and the governor card.
 */
export function overlayRacesLive(races: ControlRace[], live: Record<string, LivePrice>): ControlRace[] {
    if (Object.keys(live).length === 0) return races;

    let changed = false;
    const next = races.map((race) => {
        if (race.sameParty !== undefined) return race;
        const demProb = liveRaceDemProbability(race, live) ?? race.demProb;
        if (demProb === race.demProb) return race;
        changed = true;
        return { ...race, demProb };
    });
    // A tick that re-priced nothing keeps the array identity too, so memos
    // keyed on the race list (e.g. the carousel's columns) skip rebuilding.
    return changed ? next : races;
}

/**
 * A race's dem-keyed probability from its live party-market prices alone;
 * null when neither party market has a usable live price (the race then keeps
 * its snapshot value in every overlay).
 */
export function liveRaceDemProbability(race: ControlRace, live: Record<string, LivePrice>): number | null {
    return demWinProbabilityFromPartyProbabilities({
        dem: liveQuoteProbability(live, race.demTicker),
        rep: liveQuoteProbability(live, race.repTicker),
    });
}

/**
 * Overlays live WS quotes onto the server snapshot, preserving the snapshot's
 * dem-keyed higher-party semantics. Tickers
 * without a usable live quote keep their snapshot values.
 */
export function overlayLive(data: ChamberControlData, live: Record<string, LivePrice>): ChamberControlData {
    if (Object.keys(live).length === 0) return data;

    return {
        ...data,
        control: overlayControlLive(data.control, live),
        races: overlayRacesLive(data.races, live),
    };
}

/**
 * Re-prices the server snapshot entirely from a historical prices snapshot
 * (./midtermsSnapshot.ts), preserving the dem-keyed semantics of
 * `overlayLive`. Unlike the live overlay, tickers absent from or unpriced in
 * the snapshot become null rather than keeping their current values — a
 * historical view must never blend in live prices. House can supply races
 * already derived from its stitched historical event map; those replace the
 * current-event race list while the chamber-control markets are still
 * overlaid here.
 */
export function overlayHistorical(
    data: ChamberControlData,
    probByTicker: ReadonlyMap<string, number | null>,
    historicalRaces?: ControlRace[],
): ChamberControlData {
    const historicalProbability = (ticker: string | null): number | null =>
        ticker === null ? null : (probByTicker.get(ticker) ?? null);
    const overlayControlMarket = (market: ControlPartyMarket): ControlPartyMarket => ({
        ...market,
        probability: historicalProbability(market.ticker),
    });

    return {
        ...data,
        control: {
            dem: overlayControlMarket(data.control.dem),
            rep: overlayControlMarket(data.control.rep),
        },
        races:
            historicalRaces ??
            data.races.map((race) => {
                if (race.sameParty !== undefined) {
                    // A same-party seat is safe regardless of pricing, but only at
                    // dates where the race existed. When the snapshot knows none of
                    // its markets (relisted races, e.g. mid-cycle redistricting),
                    // null it like any unlisted race so it counts as unmapped —
                    // matching the map, which can't paint it at that date either.
                    const listed = [race.demTicker, race.repTicker].some(
                        (ticker) => ticker !== null && probByTicker.has(ticker),
                    );
                    return listed ? race : { ...race, demProb: null };
                }
                const dem = historicalProbability(race.demTicker);
                const rep = historicalProbability(race.repTicker);
                return { ...race, demProb: demWinProbabilityFromPartyProbabilities({ dem, rep }) };
            }),
    };
}

/**
 * Races rated at or above this favored probability are excluded from the WS
 * subscription: they can't cross a bucket boundary between 5-minute snapshot
 * refreshes, and skipping them keeps the house subscription bounded (~tens of
 * tickers instead of ~870). Safe races still render in the races-by-rating
 * carousel, so a Safe column mixes live prices (favored below the cutoff)
 * with snapshot-refresh prices (at or above it) — accepted trade-off.
 */
export const LIVE_SUBSCRIPTION_MAX_FAVORED_PROB = 0.95;

/** Whether the snapshot rates a race safe enough to stay off live prices (see `LIVE_SUBSCRIPTION_MAX_FAVORED_PROB`). */
export function isBeyondLiveSubscriptionCutoff(race: ControlRace): boolean {
    return race.demProb !== null && Math.max(race.demProb, 1 - race.demProb) >= LIVE_SUBSCRIPTION_MAX_FAVORED_PROB;
}

/** Both party markets of every priced competitive race (see `LIVE_SUBSCRIPTION_MAX_FAVORED_PROB`). */
export function raceSubscriptionTickers(races: readonly ControlRace[]): string[] {
    const tickers: string[] = [];
    for (const race of races) {
        if (race.demProb === null || isBeyondLiveSubscriptionCutoff(race)) continue;
        if (race.demTicker !== null) tickers.push(race.demTicker);
        if (race.repTicker !== null) tickers.push(race.repTicker);
    }
    return tickers;
}

/**
 * Party-market ticker groups for races that were unpriced in the server
 * snapshot. Each race stays a separate group so its initial WS snapshot can
 * become visible without waiting for every other unpriced race to initialize.
 */
export function unpricedRaceSubscriptionTickerGroups(races: readonly ControlRace[]): string[][] {
    return raceTickerGroups(races, (race) => race.demProb === null);
}

/** Each included race's party-market tickers as its own group; races without any are skipped. */
export function raceTickerGroups(races: readonly ControlRace[], include: (race: ControlRace) => boolean): string[][] {
    const groups: string[][] = [];
    for (const race of races) {
        if (!include(race)) continue;
        const tickers: string[] = [];
        if (race.demTicker !== null) tickers.push(race.demTicker);
        if (race.repTicker !== null) tickers.push(race.repTicker);
        if (tickers.length > 0) groups.push(tickers);
    }
    return groups;
}

/** The two party markets that determine a chamber-control headline. */
export function controlSubscriptionTickers(control: ChamberControlData['control']): string[] {
    const tickers: string[] = [];
    for (const market of [control.dem, control.rep]) {
        if (market.ticker !== null) tickers.push(market.ticker);
    }
    return tickers;
}

/** Market tickers the card watches live: both control markets plus both party markets of competitive races. */
export function liveSubscriptionTickers(data: ChamberControlData): string[] {
    const tickers = controlSubscriptionTickers(data.control);
    tickers.push(...raceSubscriptionTickers(data.races));
    return tickers;
}
